import React, { ChangeEvent, DragEvent, useState } from "react";
import Image from "next/image";

import * as styledElements from "./FileInputStyles";
import CircleBadge from "../CircleBadge";
import { v4 as uuidv4 } from "uuid";

function areFilesEqual(file1: File, file2: File) {
  return (
    file1.name === file2.name &&
    file1.size === file2.size &&
    file1.type === file2.type &&
    file1.lastModified === file2.lastModified
  );
}

const FileInput = ({
  fileExtensions,
  files,
  setFiles,
  inOrderToText,
  disabled = false,
  isMultiple = false,
  optional = false,
  width,
}: {
  fileExtensions?: string[];
  files: File[];
  setFiles: (files: File[]) => void;
  inOrderToText?: string;
  disabled?: boolean;
  isMultiple?: boolean;
  optional?: boolean;
  width?: string;
}) => {
  const [addFileId] = useState(uuidv4());

  const fileExtensionsWithDots = fileExtensions?.map((extension) => {
    return extension.startsWith(".") ? extension : `.${extension}`;
  });

  const fileMatchesExtensions = (file: File) => {
    if (fileExtensions !== undefined) {
      return fileExtensions.some((extension) =>
        file.name.toLowerCase().endsWith(extension.toLowerCase()),
      );
    }
    return true;
  };

  const handleAddFiles = (filesToAdd: FileList | null) => {
    if (filesToAdd === null) {
      setFiles([]);
    } else {
      const listOfFileIndexes = Array.from(
        { length: filesToAdd.length },
        (_, index) => index,
      );
      setFiles(
        listOfFileIndexes
          .map((fileIndex) => filesToAdd[fileIndex] as File)
          .filter(fileMatchesExtensions),
      );
    }
  };

  const handleAddMoreFiles = (filesToAdd: FileList | null) => {
    if (filesToAdd !== null) {
      const listOfFileIndexes = Array.from(
        { length: filesToAdd.length },
        (_, index) => index,
      );
      setFiles([
        ...files,
        ...listOfFileIndexes
          .map((fileIndex) => filesToAdd[fileIndex] as File)
          .filter(fileMatchesExtensions)
          .filter(
            (newFile) => !files.some((file) => areFilesEqual(file, newFile)),
          ),
      ]);
    }
  };

  const handleDropEvent = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    if (isMultiple) {
      handleAddMoreFiles(event.dataTransfer.files);
    } else {
      handleAddFiles(event.dataTransfer.files);
    }
  };

  const handleChangeEvent = (event: ChangeEvent<HTMLInputElement>) => {
    if (isMultiple) {
      handleAddMoreFiles(event.target.files);
    } else {
      handleAddFiles(event.target.files);
    }
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setFiles(
      files.filter((file) => {
        return !areFilesEqual(file, fileToRemove);
      }),
    );
  };

  const handleDragOver = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
  };

  const fileOrFiles = isMultiple ? "file(s)" : "file";
  const addLabel = fileExtensionsWithDots
    ? `Add ${fileExtensionsWithDots
        .map((extension) => extension.toUpperCase())
        .join(" or ")} ${fileOrFiles}`
    : `Add ${fileOrFiles}`;

  const hasFile = files.length > 0;
  const hasMultiple = files.length > 1;

  return (
    <styledElements.Container
      hasFile={hasFile}
      disabled={disabled}
      htmlFor={addFileId}
      onDrop={handleDropEvent}
      onDragOver={handleDragOver}
      width={width}
    >
      <input
        onChange={handleChangeEvent}
        name={addLabel}
        value={""}
        accept={fileExtensionsWithDots?.join(", ")}
        multiple={isMultiple}
        type={"file"}
        id={addFileId}
        style={{ display: "none" }}
      />
      <Image
        src={hasFile ? "/icons/file_uploaded.svg" : "/icons/upload_gray.svg"}
        alt="upload file"
        width="80"
        height="80"
      />
      {hasFile ? (
        <>
          <styledElements.FileListItems hasMultiple={hasMultiple}>
            {files.map((file, fileIndex) => {
              return (
                <styledElements.FileListItem
                  key={`${file.name}-${fileIndex}`}
                  addSeparator={hasMultiple && fileIndex + 1 < files.length}
                >
                  <CircleBadge
                    iconProps={{
                      src: "/icons/cancel.svg",
                      alt: "cancel",
                    }}
                    onClick={(event) => {
                      event?.preventDefault();
                      handleRemoveFile(file);
                    }}
                  />
                  <styledElements.FileListName
                    smallText={file.name.length > 28}
                  >
                    {file.name}
                  </styledElements.FileListName>
                </styledElements.FileListItem>
              );
            })}
          </styledElements.FileListItems>
          {isMultiple && (
            <styledElements.AddMoreFiles>
              <Image
                src={`/icons/upload_gray.svg`}
                alt="upload file"
                width="20"
                height="20"
                style={{ marginRight: "1rem" }}
              />
              Add more files
            </styledElements.AddMoreFiles>
          )}
        </>
      ) : (
        <styledElements.Label>{`${addLabel} ${inOrderToText || ""} ${
          optional ? "(optional)" : ""
        }`}</styledElements.Label>
      )}
    </styledElements.Container>
  );
};

export default FileInput;
